import React, { useState, useEffect, useRef } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, SimpleGrid,
  FormLabel, FormControl, HStack, Badge, Icon, useToast,
} from '@chakra-ui/react';
import { FiImage } from 'react-icons/fi';
import { herramientasApi, catalogosApi, Herramienta, CatalogoItem, UPLOADS_BASE } from '../../../api/serviciosApi';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: Herramienta;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const ESTADOS_FISICOS = ['Nuevo', 'Bueno', 'Regular', 'Malo', 'Dañado'];
const ESTATUS = ['activo', 'inactivo'];

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box borderBottom="2px solid" borderColor="#5b182b" pb={1} mb={1} mt={3}>
    <Text fontSize="xs" fontWeight="bold" color="#5b182b" textTransform="uppercase" letterSpacing="wide">{children}</Text>
  </Box>
);

const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" fontWeight="bold">{label}</Text>
    <Text fontSize="sm">{value || '—'}</Text>
  </Box>
);

const dateOnly = (v?: string) => (v ? v.split('T')[0] : '');

const FormularioHerramienta: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [categorias, setCategorias] = useState<CatalogoItem[]>([]);
  const [guardando, setGuardando] = useState(false);

  const [sku, setSku] = useState(data?.sku || '');
  const [nombre, setNombre] = useState(data?.nombre || '');
  const [categoriaId, setCategoriaId] = useState(data?.categoriaId != null ? String(data.categoriaId) : '');
  const [marca, setMarca] = useState(data?.marca || '');
  const [modelo, setModelo] = useState(data?.modelo || '');
  const [numeroSerie, setNumeroSerie] = useState(data?.numeroSerie || '');
  const [estadoFisico, setEstadoFisico] = useState(data?.estadoFisico || '');
  const [cantidad, setCantidad] = useState(data?.cantidad != null ? String(data.cantidad) : '');
  const [ubicacion, setUbicacion] = useState(data?.ubicacion || '');
  const [responsable, setResponsable] = useState(data?.responsable || '');
  const [fechaAdquisicion, setFechaAdquisicion] = useState(dateOnly(data?.fechaAdquisicion));
  const [observaciones, setObservaciones] = useState(data?.observaciones || '');
  const [estatus, setEstatus] = useState(data?.estatus || 'activo');
  const [fotografia, setFotografia] = useState<File | null>(null);

  useEffect(() => {
    catalogosApi.categoriasHerramienta().then(setCategorias).catch(() => setCategorias([]));
  }, []);

  const handleGuardar = async () => {
    if (!sku || !nombre) {
      toast({ title: 'Campos obligatorios', description: 'SKU y nombre son requeridos.', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const fd = new FormData();
      fd.append('sku', sku);
      fd.append('nombre', nombre);
      if (categoriaId) fd.append('categoriaId', categoriaId);
      fd.append('marca', marca);
      fd.append('modelo', modelo);
      fd.append('numeroSerie', numeroSerie);
      fd.append('estadoFisico', estadoFisico);
      if (cantidad !== '') fd.append('cantidad', cantidad);
      // En alta, la existencia disponible inicia igual a la cantidad
      if (modo === 'new' && cantidad !== '') fd.append('cantidadDisponible', cantidad);
      fd.append('ubicacion', ubicacion);
      fd.append('responsable', responsable);
      if (fechaAdquisicion) fd.append('fechaAdquisicion', fechaAdquisicion);
      fd.append('observaciones', observaciones);
      fd.append('estatus', estatus);
      if (fotografia) fd.append('fotografia', fotografia);

      if (modo === 'edit' && data) {
        await herramientasApi.update(data.id, fd);
      } else {
        await herramientasApi.create(fd);
      }
      toast({ title: modo === 'edit' ? 'Herramienta actualizada' : 'Herramienta registrada', status: 'success', duration: 3000 });
      onGuardado?.();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'Error al guardar.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  const fmt = (v?: string) => (v ? new Date(v).toLocaleDateString('es-MX') : '—');

  if (modo === 'view') {
    const d = data || ({} as Herramienta);
    return (
      <VStack spacing={3} align="stretch">
        {d.fotografia && (
          <Box>
            <img src={`${UPLOADS_BASE}${d.fotografia}`} alt={d.nombre} style={{ maxHeight: 160, borderRadius: 8, objectFit: 'cover' }} />
          </Box>
        )}
        <SectionTitle>Datos de la herramienta</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="SKU" value={d.sku} />
          <Field label="NOMBRE" value={d.nombre} />
          <Field label="CATEGORÍA" value={d.categoria?.nombre} />
          <Field label="MARCA / MODELO" value={[d.marca, d.modelo].filter(Boolean).join(' ')} />
          <Field label="No. SERIE" value={d.numeroSerie} />
          <Field label="ESTADO FÍSICO" value={<Badge>{d.estadoFisico}</Badge>} />
          <Field label="CANTIDAD" value={d.cantidad} />
          <Field label="DISPONIBLE" value={d.cantidadDisponible} />
          <Field label="UBICACIÓN" value={d.ubicacion} />
          <Field label="RESPONSABLE" value={d.responsable} />
          <Field label="ADQUISICIÓN" value={fmt(d.fechaAdquisicion)} />
          <Field label="ESTATUS" value={<Badge colorScheme={d.estatus === 'activo' ? 'green' : 'gray'}>{d.estatus}</Badge>} />
          <Box gridColumn={{ md: 'span 2' }}>
            <Field label="OBSERVACIONES" value={d.observaciones} />
          </Box>
        </SimpleGrid>
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos de la herramienta</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">SKU / Clave</FormLabel>
          <Input value={sku} onChange={(e) => setSku(e.target.value)} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Nombre</FormLabel>
          <Input value={nombre} onChange={(e) => setNombre(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Categoría</FormLabel>
          <Select placeholder="Selecciona categoría" value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)}>
            {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estado físico</FormLabel>
          <Select placeholder="Selecciona" value={estadoFisico} onChange={(e) => setEstadoFisico(e.target.value)}>
            {ESTADOS_FISICOS.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Marca</FormLabel>
          <Input value={marca} onChange={(e) => setMarca(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Modelo</FormLabel>
          <Input value={modelo} onChange={(e) => setModelo(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Número de serie</FormLabel>
          <Input value={numeroSerie} onChange={(e) => setNumeroSerie(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Cantidad {modo === 'edit' && '(total)'}</FormLabel>
          <Input type="number" min={0} value={cantidad} onChange={(e) => setCantidad(e.target.value)} />
        </FormControl>
        {modo === 'edit' && (
          <FormControl>
            <FormLabel fontSize="sm">Disponible</FormLabel>
            <Input value={data?.cantidadDisponible ?? 0} isReadOnly bg="gray.100" />
            <Text fontSize="2xs" color="gray.500" mt={1}>Se ajusta con los movimientos de inventario.</Text>
          </FormControl>
        )}
        <FormControl>
          <FormLabel fontSize="sm">Ubicación</FormLabel>
          <Input value={ubicacion} onChange={(e) => setUbicacion(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Responsable</FormLabel>
          <Input value={responsable} onChange={(e) => setResponsable(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha de adquisición</FormLabel>
          <Input type="date" value={fechaAdquisicion} onChange={(e) => setFechaAdquisicion(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={estatus} onChange={(e) => setEstatus(e.target.value)}>
            {ESTATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Observaciones</FormLabel>
          <Textarea rows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Fotografía</SectionTitle>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => setFotografia(e.target.files?.[0] || null)} />
      <Box border="2px dashed" borderColor="gray.300" borderRadius="md" p={4} textAlign="center" cursor="pointer"
        _hover={{ borderColor: '#5b182b', bg: 'red.50' }} onClick={() => fileRef.current?.click()}>
        {fotografia ? (
          <HStack justify="center"><Icon as={FiImage} color="green.500" /><Text fontSize="sm">{fotografia.name}</Text></HStack>
        ) : data?.fotografia && modo === 'edit' ? (
          <HStack justify="center"><Icon as={FiImage} color="gray.500" /><Text fontSize="sm" color="gray.500">Foto actual cargada — clic para reemplazar</Text></HStack>
        ) : (
          <Text fontSize="sm" color="gray.500">Haz clic para seleccionar una imagen</Text>
        )}
      </Box>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">Guardar</Button>
      </HStack>
    </VStack>
  );
};

export default FormularioHerramienta;
